// [S-063] Re-attestation schedule data hooks.
//
// Backs apps/web/src/screens/ReAttestation.tsx. Reads the platform-wide
// schedule from `GET /platform/re-attestation/schedule` (the endpoint
// landed in S-063 backend PR #180) and writes scheduling actions via
// `POST /platform/re-attestation/templates/:id/schedule`.
//
// Both endpoints are platform_admin-only on the server. The UI route is
// also gated via <RequireRole minRole="platform_admin"> so a tenant_admin
// or tenant_user can't even see the page; this is defense-in-depth so a
// route-guard regression doesn't leak the surface.
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApi } from "./api";

export type ScheduleRow = {
  readonly id: string;
  readonly dealType: string;
  readonly guild: string;
  readonly tier: string;
  readonly lifecycleState: string;
  readonly effectiveFrom: string;
  readonly anchorCBA: string;
  readonly reAttestationDueAt: string;
  readonly submittedBy: string;
  readonly excludedTenantCount: number;
};

export type ScheduleResponse = {
  schedule: ScheduleRow[];
};

export const RE_ATTESTATION_SCHEDULE_KEY = [
  "platform",
  "re-attestation",
  "schedule",
] as const;

export function useReAttestationSchedule(options?: {
  // The endpoint is platform_admin-only; pass `enabled: false` from surfaces
  // shown to other roles (e.g. the Home dashboard, which renders for everyone)
  // so a tenant user never fires a query that would 403.
  enabled?: boolean;
}): UseQueryResult<ScheduleResponse, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: RE_ATTESTATION_SCHEDULE_KEY,
    enabled: options?.enabled ?? true,
    queryFn: () =>
      api<ScheduleResponse>("/platform/re-attestation/schedule"),
  });
}

export type ScheduleReAttestationInput = {
  templateId: string;
  scheduledFor: string; // ISO datetime
  note: string | null;
};

export function useScheduleReAttestation(): UseMutationResult<
  void,
  Error,
  ScheduleReAttestationInput
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ templateId, scheduledFor, note }) => {
      await api<void>(
        `/platform/re-attestation/templates/${encodeURIComponent(templateId)}/schedule`,
        {
          method: "POST",
          body: { scheduledFor, note },
        }
      );
    },
    onSuccess: () => {
      // Invalidate the schedule so the row's due-date / bucket counts
      // pick up the change next paint. Scheduling doesn't transition
      // template state (that's S-031 / S-064), so we don't invalidate
      // any template-related queries here.
      void qc.invalidateQueries({ queryKey: RE_ATTESTATION_SCHEDULE_KEY });
    },
  });
}
